// Build the almanac.v1 JSON export payload from the in-browser pipelineState.
// Pure function — no I/O, no side effects. Consumed by the "Download JSON"
// button in the report header (and eventually the sidebar nav rail).
//
// Schema: almanac.v1 (documented in docs/planning/JSON_DOWNLOAD_MVP.md).
// Designed to be version-stable — future schemas bump to almanac.v2 and v1
// readers can refuse or migrate. Same payload feeds the eventual Excel
// multi-sheet export and any v2 re-import flow.

const SCHEMA_VERSION = "almanac.v1";
const GENERATOR_VERSION = "1.0.0";

export function buildAlmanacExport(pipelineState) {
  if (!pipelineState) {
    throw new Error("buildAlmanacExport: pipelineState is required");
  }
  const { platform, leagueMetadata, seasons, matchups, championshipsByYear, stats, aliasMap } = pipelineState;
  const years = stats?.meta?.yearsCovered || [];
  const firstYear = years.length ? Number(years[0]) : null;
  const lastYear = years.length ? Number(years[years.length - 1]) : null;

  // League ID source differs by platform; both platforms attach it to the
  // first season payload alongside other identifiers.
  const leagueId = platform === "yahoo"
    ? (seasons[0]?.league_key || null)
    : (seasons[0]?.league_id || null);

  return {
    schema: SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    generator: {
      name: "League Almanac",
      url: "https://mock1.app/almanac",
      version: GENERATOR_VERSION,
    },
    league: {
      platform,
      name: leagueMetadata?.name || "Your League",
      id: leagueId,
      yearRange: { first: firstYear, last: lastYear },
      seasonCount: seasons.length,
      format: {
        scoringType: detectScoringType(seasons, platform),
        draftType: stats?.drafts?.[0]?.draftType || null,
        teamCount: stats?.meta?.totalOwners ?? null,
        rosterSize: detectRosterSize(seasons, platform),
        medianWins: !!stats?.meta?.formatHasMedianWins,
        medianWinsFirstYear: stats?.meta?.formatMedianFirstYear || null,
        keeperRules: null,
      },
      owners: buildOwnerList(seasons, aliasMap || {}, platform),
    },
    stats,
    seasons: years.map((y) =>
      serializeSeason(String(y), { matchups, stats, championshipsByYear, seasonsRaw: seasons, platform })
    ),
  };
}

// Scoring-type detector — looks at the first season's scoring config and
// classifies as standard / half_ppr / ppr / custom. Falls back to "custom"
// when the value doesn't match a standard preset or when configs aren't
// available (Yahoo path doesn't always carry stat_modifiers through).
function detectScoringType(seasons, platform) {
  const s0 = seasons?.[0];
  if (!s0) return null;
  if (platform === "sleeper") {
    const rec = s0.scoring_settings?.rec;
    if (rec == null) return "custom";
    if (rec === 0) return "standard";
    if (rec === 0.5) return "half_ppr";
    if (rec === 1) return "ppr";
    return "custom";
  }
  if (platform === "yahoo") {
    // Yahoo's scoring lives in _raw_settings; the per-season payload doesn't
    // currently carry it through the import pipeline (only settings-import
    // mode does). Return "custom" until we plumb scoring through.
    const stats = s0._raw_settings?.fantasy_content?.league?.[1]?.settings?.[0]?.stat_modifiers?.stats;
    if (!Array.isArray(stats)) return "custom";
    const rec = stats.find((s) => Number(s.stat?.stat_id) === 11);
    const v = rec?.stat?.value != null ? parseFloat(rec.stat.value) : null;
    if (v == null) return "custom";
    if (v === 0) return "standard";
    if (v === 0.5) return "half_ppr";
    if (v === 1) return "ppr";
    return "custom";
  }
  return null;
}

// Roster size — actual roster slot count, which is what fans mean by "16-man
// roster". Different from draft rounds (dynasty rookie drafts are only 3-5
// rounds). Sleeper exposes roster_positions array directly per season.
// Yahoo carries it under _raw_settings.
function detectRosterSize(seasons, platform) {
  const s0 = seasons?.[0];
  if (!s0) return null;
  if (platform === "sleeper") {
    return Array.isArray(s0.roster_positions) ? s0.roster_positions.length : null;
  }
  if (platform === "yahoo") {
    const positions = s0._raw_settings?.fantasy_content?.league?.[1]?.settings?.[0]?.roster_positions;
    if (!Array.isArray(positions)) return null;
    let total = 0;
    for (const p of positions) {
      const count = parseInt(p?.roster_position?.count ?? p?.count ?? 0, 10);
      if (!isNaN(count)) total += count;
    }
    return total || null;
  }
  return null;
}

// Owner aliases — collect every raw display name seen across all seasons,
// group by canonical (via aliasMap), emit per-canonical aliases list.
// Canonical names always present; aliases array omitted when empty so the
// JSON stays clean for leagues where no aliases were edited.
function buildOwnerList(seasons, aliasMap, platform) {
  const rawSeen = new Set();
  for (const s of seasons || []) {
    if (platform === "sleeper") {
      for (const u of s.users || []) {
        if (u.display_name) rawSeen.add(u.display_name);
      }
    } else if (platform === "yahoo") {
      for (const t of s.teams || []) {
        const raw = t.nickname || t.name;
        if (raw) rawSeen.add(raw);
      }
    }
  }
  const byCanonical = new Map();
  for (const raw of rawSeen) {
    const canonical = aliasMap[raw] || raw;
    if (!byCanonical.has(canonical)) byCanonical.set(canonical, new Set());
    byCanonical.get(canonical).add(raw);
  }
  return [...byCanonical.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([canonical, aliasSet]) => {
      const aliases = [...aliasSet].filter((a) => a !== canonical).sort();
      return aliases.length ? { canonical, aliases } : { canonical };
    });
}

// Per-season slice — pulls from already-normalized stats payload (drafts,
// trades) plus derived standings. Raw season payloads are deliberately NOT
// passed through; they include the ~24MB Sleeper players-DB cache (Sleeper)
// and other internal fields that would bloat the export 10-20x with no
// archival value.
//
// drafts is PLURAL per season — Sleeper dynasty leagues have both startup
// and rookie drafts in their founding year, and rookie drafts continue
// every subsequent year. The spec's singular `draft` would lose data.
function serializeSeason(yearStr, { matchups, stats, championshipsByYear, seasonsRaw, platform }) {
  const seasonMatchups = (matchups || []).filter((m) => String(m.year) === yearStr);
  const seasonDrafts = (stats?.drafts || []).filter((d) => String(d.year) === yearStr);
  const seasonTrades = (stats?.trades || []).filter((t) => String(t.season) === yearStr);
  const champ = championshipsByYear?.[yearStr] || null;
  // Yahoo seasons carry standings natively; Sleeper does not. Derive a
  // uniform shape from matchups + championship hint regardless of platform.
  // Standings derivation uses TOTAL wins (H2H + median when format applies)
  // so the export matches what the report displays.
  const standings = deriveStandings(seasonMatchups, champ);

  return {
    year: yearStr,
    standings,
    matchups: seasonMatchups,
    drafts: seasonDrafts,
    transactions: {
      trades: seasonTrades,
    },
    // Yahoo-native standings preserved as raw passthrough when available,
    // for consumers who prefer the platform's authoritative ranking.
    standingsRaw: platform === "yahoo"
      ? (seasonsRaw.find((s) => String(s.season) === yearStr)?.standings || null)
      : null,
  };
}

// Standings derivation — sums regular-season W-L-PF per owner from the
// matchups array, layers in playoff finish from championshipsByYear. Output
// shape: [{ owner, w, l, pf, finish }, ...] sorted by W desc then PF desc.
// finish: 1 = champion, 2 = runner-up, 3 = third, null = didn't medal.
export function deriveStandings(matchups, champ) {
  if (!Array.isArray(matchups) || !matchups.length) return [];
  const reg = matchups.filter((m) => !m.playoff && !m.consolation);
  const byOwner = new Map();
  for (const m of reg) {
    if (!m.owner) continue;
    if (!byOwner.has(m.owner)) byOwner.set(m.owner, { owner: m.owner, w: 0, l: 0, pf: 0 });
    const r = byOwner.get(m.owner);
    r.pf += m.pts || 0;
    if (m.won) r.w++; else r.l++;
  }
  const rows = [...byOwner.values()];
  for (const r of rows) {
    if (champ?.champion === r.owner) r.finish = 1;
    else if (champ?.runnerUp === r.owner) r.finish = 2;
    else if (champ?.third === r.owner) r.finish = 3;
    else r.finish = null;
  }
  rows.sort((a, b) => (b.w - a.w) || (b.pf - a.pf));
  return rows;
}
